import React from "react";
import {
    ModuleExpanded as StyledModuleExpanded,
    ModuleDescription,
    PrerequisitesTitle,
    PrerequisitesContainer,
    PrerequisiteWrapper,
    PrerequisiteRow,
    PrerequisiteLeft,
    PrerequisiteName,
    PrerequisiteRight,
    ModuleSemester,
    ModulePrice,
    ActionButton
} from "../styles/primitives";
import StatusBadge from "./StatusBadge";
import { getModuleDescription, isModuleSelectedByIdOrName, deriveModuleSource, countsTowardsModuleLimit } from "../../shared/utils/moduleHelpers";

const ModuleExpanded = ({
    module,
    visiblePrerequisites,
    isEditMode,
    selectedModules,
    isSelectionDisabled,
    onRemoveModule,
    onAddModule,
    allModules
}) => {
    const moduleDescriptionText = getModuleDescription(module);
    return (
        <StyledModuleExpanded>
            {moduleDescriptionText && (
                <ModuleDescription>{moduleDescriptionText}</ModuleDescription>
            )}
            
            {visiblePrerequisites && visiblePrerequisites.length > 0 && (
                <PrerequisiteWrapper>
                    <PrerequisitesTitle>Prerequisites</PrerequisitesTitle>
                    <PrerequisitesContainer>
                        {visiblePrerequisites.map(([prereqId, prereq], prereqIdx, prereqArr) => {
                            const prereqStatusRaw = prereq.reason || prereq.status || "";
                            const prereqStatus = prereqStatusRaw.toUpperCase();
                            const isFailed = prereqStatus === "FAIL" || prereqStatus === "FAILED";

                            const allModulesArray = Array.isArray(allModules)
                                ? allModules
                                : Object.values(allModules || {});

                            // Find the canonical module that this prerequisite refers to
                            const matchingModule = allModulesArray.find((m) => {
                                const moduleIdMatches =
                                    m.module_id &&
                                    String(m.module_id) === String(prereqId);

                                const netsuiteIdMatches =
                                    m.netsuite_product_id &&
                                    String(m.netsuite_product_id) === String(prereqId);

                                const codeMatches =
                                    (m.Code || m.code) &&
                                    prereq.code &&
                                    String(m.Code || m.code) === String(prereq.code);

                                // Match by module_id, netsuite_product_id, or code
                                return moduleIdMatches || netsuiteIdMatches || codeMatches;
                            });

                            const selectionId = matchingModule
                                ? matchingModule.netsuite_product_id
                                : prereqId;
                            const selectionName = matchingModule
                                ? matchingModule.name
                                : prereq.name;

                            const isPrereqSelected = isModuleSelectedByIdOrName(
                                selectionId,
                                selectionName,
                                selectedModules,
                                allModulesArray
                            );

                            const isLast = prereqIdx === prereqArr.length - 1;
                            const prereqSource = deriveModuleSource({ ...prereq, status: prereq.reason });
                            const shouldDisablePrereqAdd =
                                isSelectionDisabled &&
                                countsTowardsModuleLimit(prereqSource) &&
                                !isPrereqSelected;

                            // Determine if prerequisite is core or elective
                            const prereqType = matchingModule?.type || prereq.type;
                            const prereqIsCore = matchingModule?.is_core || prereq.is_core;
                            const isPrereqCore = prereqType === "required" || prereqType === "core" || prereqType === "foundational" || prereqIsCore || prereqType !== "elective";

                            // Use matching module's status if available, otherwise use prerequisite's status
                            // This ensures that if a matching eligible module exists, it shows "eligible" instead of "not enrolled"
                            const effectiveStatus = matchingModule 
                                ? (matchingModule.status || matchingModule.reason || prereq.reason || prereq.status)
                                : (prereq.reason || prereq.status);

                            return (
                                <PrerequisiteRow 
                                    key={prereqId}
                                    $isFailed={isFailed}
                                    $isLast={isLast}
                                >
                                    <PrerequisiteLeft>
                                        {isPrereqCore ? (
                                            <i
                                                title="Core"
                                                className="fa-light fa-circle-exclamation-check"
                                                style={{ color: "var(--purple)", fontSize: "17px" }}
                                            />
                                        ) : (
                                            <i
                                                title="Elective"
                                                className="fa-light fa-arrow-progress"
                                                style={{ color: "var(--teal)", fontSize: "17px" }}
                                            />
                                        )}
                                        <PrerequisiteName>{prereq.name}</PrerequisiteName>
                                        <StatusBadge status={effectiveStatus} />
                                    </PrerequisiteLeft>
                                    <PrerequisiteRight>
                                    <div style={{display: "flex", flexDirection: "row", alignItems: "center", gap: "12px"}}>
                                        <ModuleSemester>
                                            <i className="fa-light fa-clock" style={{ color: "var(--button-color)" }}></i>
                                            <span>{prereq.term === "1, 2" ? "Year Module" : `Semester ${prereq.term || 1}`}</span>
                                        </ModuleSemester>
                                        <ModulePrice>
                                            {new Intl.NumberFormat("en-ZA", {
                                                style: "currency",
                                                currency: "ZAR",
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            }).format(Number(matchingModule?.price || prereq.price ) || 0)}
                                        </ModulePrice>
                                        </div>
                                        {isEditMode && (
                                            isPrereqSelected ? (
                                            <ActionButton 
                                                $variant="delete"
                                                onClick={(e) => { 
                                                    e.stopPropagation(); 
                                                    onRemoveModule(
                                                            matchingModule || {
                                                                name: prereq.name,
                                                                netsuite_product_id: selectionId,
                                                                module_id: prereqId,
                                                                Code: prereq.code,
                                                                year: prereq.year,
                                                                term: prereq.term,
                                                                status: prereq.reason || prereq.status
                                                            }
                                                        ); 
                                                }}
                                                type="button"
                                                title="Remove from selection"
                                                aria-label="Remove prerequisite from selection"
                                            >
                                                <i className="fa-light fa-trash-alt"></i>
                                            </ActionButton>
                                        ) : (
                                            <ActionButton 
                                                $variant="add"
                                                $disabled={shouldDisablePrereqAdd}
                                                onClick={(e) => { 
                                                    e.stopPropagation(); 
                                                    onAddModule(
                                                            matchingModule || {
                                                                name: prereq.name, 
                                                                status: prereq.reason, 
                                                                netsuite_product_id: selectionId,
                                                                module_id: prereqId,
                                                                Code: prereq.code,
                                                                year: prereq.year,
                                                                term: prereq.term
                                                            }
                                                        ); 
                                                }}
                                                type="button"
                                                title="Add to selection"
                                                aria-label="Add prerequisite to selection"
                                                disabled={shouldDisablePrereqAdd}
                                            >
                                                <i className="fal fa-plus"></i>
                                            </ActionButton>
                                        )
                                    )}
                                    </PrerequisiteRight>
                                </PrerequisiteRow>
                            );
                        })}
                    </PrerequisitesContainer>
                </PrerequisiteWrapper>
            )}
        </StyledModuleExpanded>
    );
};

export default ModuleExpanded;
